import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { forkJoin } from 'rxjs';
import { SupportTicket, TicketCategory, TicketListState, TicketRaiseContext, TicketSummary } from '../models/ticket.model';
import { TicketService } from '../services/ticket.service';

@Component({ selector: 'ft-ticket-center', templateUrl: './ticket-center.component.html', styleUrls: ['./ticket-center.component.scss', './ticket-summary.component.scss'], standalone: false })
export class TicketCenterComponent implements OnChanges {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  context: TicketRaiseContext | null = null;
  categories: TicketCategory[] = [];
  tickets: SupportTicket[] = [];
  selectedTicket: SupportTicket | null = null;
  state: TicketListState = 'all';
  summary: TicketSummary = { total: 0, pending: 0, inProgress: 0, closed: 0 };
  summaryLoading = false;
  selectedYear = String(new Date().getFullYear());
  selectedMonth = '';
  readonly yearOptions = this.buildYearOptions();
  readonly monthOptions = [
    { label: 'January', value: '1' }, { label: 'February', value: '2' },
    { label: 'March', value: '3' }, { label: 'April', value: '4' },
    { label: 'May', value: '5' }, { label: 'June', value: '6' },
    { label: 'July', value: '7' }, { label: 'August', value: '8' },
    { label: 'September', value: '9' }, { label: 'October', value: '10' },
    { label: 'November', value: '11' }, { label: 'December', value: '12' },
  ];
  page = 0;
  pageSize = 10;
  totalPages = 0;
  totalElements = 0;
  search = '';
  loading = false;
  loadingContext = false;
  showCreate = false;
  errorMessage = '';

  constructor(private ticketService: TicketService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) this.initialize();
  }

  initialize(): void {
    this.showCreate = false; this.selectedTicket = null; this.state = 'all'; this.page = 0; this.errorMessage = ''; this.loadingContext = true;
    forkJoin({ context: this.ticketService.getRaiseContext(), categories: this.ticketService.getCategories() }).subscribe({
      next: result => { this.context = result.context; this.categories = result.categories; this.loadingContext = false; this.loadSummary(); this.loadTickets(); },
      error: error => { this.loadingContext = false; this.errorMessage = this.error(error, 'You do not have access to ticketing.'); },
    });
  }

  loadTickets(): void {
    this.loading = true; this.errorMessage = '';
    const range = this.monthRange();
    this.ticketService.getTickets(this.state, this.page, this.pageSize, this.search, range.from, range.to).subscribe({
      next: result => { this.tickets = result.content; this.page = result.page; this.totalPages = result.totalPages; this.totalElements = result.totalElements; this.loading = false; },
      error: error => { this.loading = false; this.tickets = []; this.errorMessage = this.error(error, 'Unable to load tickets.'); },
    });
  }

  loadSummary(): void {
    this.summaryLoading = true;
    const range = this.monthRange();
    this.ticketService.getSummary(range.from, range.to).subscribe({
      next: summary => { this.summary = summary; this.summaryLoading = false; },
      error: () => { this.summaryLoading = false; },
    });
  }

  refresh(): void { this.loadSummary(); this.loadTickets(); }
  applyMonth(): void { this.page = 0; this.refresh(); }
  applyYear(): void { this.page = 0; this.refresh(); }

  changeState(state: TicketListState): void { if (this.state === state) return; this.state = state; this.page = 0; this.loadTickets(); }
  stateCount(state: TicketListState): number {
    if (state === 'all') return this.summary.total;
    if (state === 'pending') return this.summary.pending;
    if (state === 'in_progress') return this.summary.inProgress;
    if (state === 'closed') return this.summary.closed;
    return this.summary.pending + this.summary.inProgress;
  }
  searchTickets(): void { this.page = 0; this.loadTickets(); }
  clearSearch(): void { this.search = ''; this.searchTickets(); }
  previousPage(): void { if (this.page > 0) { this.page--; this.loadTickets(); } }
  nextPage(): void { if (this.page + 1 < this.totalPages) { this.page++; this.loadTickets(); } }
  firstPage(): void { if (this.page !== 0) { this.page = 0; this.loadTickets(); } }
  lastPage(): void { if (this.totalPages > 0 && this.page !== this.totalPages - 1) { this.page = this.totalPages - 1; this.loadTickets(); } }

  viewTicket(ticket: SupportTicket): void {
    this.loading = true;
    this.ticketService.getTicket(ticket.id).subscribe({
      next: detail => { this.selectedTicket = detail; this.loading = false; },
      error: error => { this.loading = false; this.errorMessage = this.error(error, 'Unable to open the ticket.'); },
    });
  }

  onCreated(_: SupportTicket): void { this.showCreate = false; this.page = 0; this.state = 'all'; this.loadSummary(); this.loadTickets(); }
  onTicketChanged(ticket: SupportTicket): void { this.selectedTicket = ticket; this.loadSummary(); this.loadTickets(); }
  close(): void { this.visible = false; this.visibleChange.emit(false); }
  closeChild(): void { this.showCreate = false; this.selectedTicket = null; }
  statusClass(status: string): string { return `status-${status.toLowerCase().replace(/_/g, '-')}`; }
  displayStatus(status: string): string { return status.replace(/_/g, ' '); }
  priorityClass(priority: string): string { return `priority-${priority.toLowerCase()}`; }
  trackTicket(_: number, ticket: SupportTicket): number { return ticket.id; }

  private monthRange(): { from?: number; to?: number } {
    if (!this.selectedYear) return {};
    const year = Number(this.selectedYear);
    if (!this.selectedMonth) {
      return { from: new Date(year, 0, 1).getTime(), to: new Date(year + 1, 0, 1).getTime() };
    }
    const month = Number(this.selectedMonth);
    return { from: new Date(year, month - 1, 1).getTime(), to: new Date(year, month, 1).getTime() };
  }

  private buildYearOptions(): string[] {
    const currentYear = new Date().getFullYear();
    return [String(currentYear), String(currentYear - 1)];
  }

  private error(error: unknown, fallback: string): string { const value = error as { error?: { message?: string } }; return value?.error?.message || fallback; }
}
